import { Play } from 'lucide-react'
import { useState, type CSSProperties } from 'react'

import { BASE_URL } from '../../../api/client'
import { MachineVideoOverlay } from '../../knowledge-base/MachineVideoOverlay'
import { TOKENS } from '../../ui/dashboard-primitives'
import { ArtifactAction } from './ArtifactAction'
import { ARTIFACT_EYEBROW, ARTIFACT_NAME, ARTIFACT_SURFACE } from './styles'
import type { VideoArtifactData } from './types'

// Same shell as the exercise card: V3's media card is ARTIFACT_SURFACE with the
// padding zeroed and the media clipped — no separate values.
const SHELL: CSSProperties = {
  ...ARTIFACT_SURFACE,
  padding: 0,
  overflow: 'hidden',
}

const STAGE: CSSProperties = {
  position: 'relative',
  aspectRatio: '16 / 9',
  background:
    'radial-gradient(80% 80% at 50% 40%, ' + TOKENS.creamHairline + ', transparent), rgba(28, 24, 22, 0.55)',
}

const POSTER: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  // `contain`, not `cover`: a machine photo cropped to 16/9 loses the station.
  objectFit: 'contain',
  padding: 12,
  boxSizing: 'border-box',
  opacity: 0.85,
}

/**
 * Geometry only — deliberately no `background`, `border` or `boxShadow`.
 *
 * An inline value beats every class, so putting the fill here would silently
 * defeat `hover:bg-*`, and putting the shadow here would defeat
 * `focus-visible:ring-2` (a box-shadow in Tailwind v4) — the class would be
 * present, the test would pass, and neither state would ever paint. `css: false`
 * in vitest.config.ts means no unit test in this repo can see that. The fill,
 * outline and shadow live in the button's classes instead.
 */
const PLAY_BUTTON: CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 56,
  height: 56,
  borderRadius: 9999,
  cursor: 'pointer',
}

/**
 * `video` — a locally cached setup clip: poster and a play affordance.
 *
 * The player is `MachineVideoOverlay`, imported unchanged. It is already what the
 * Knowledge Base uses for `/media` clips, focus trap, Escape handling and portal
 * included, so a bespoke player here would be a second thing to keep correct.
 *
 * **There is no duration badge.** The V3 mockup shows a `0:42` chip, but
 * `duration_seconds` was dropped from the payload on 2026-08-08 (the ruling at
 * the top of Spec I) because both branches of the video resolver hardcoded it to
 * `None`, so the chip could never fire. `extra="forbid"` server-side means
 * nothing can send one, which would make a badge here permanently dead code.
 *
 * A missing or dead poster is a normal state, not an edge case — only about half
 * the Technogym registry has a cached asset — so the play affordance stands on
 * the gradient alone and never depends on the image having loaded.
 */
export function VideoArtifactCard({ data }: { data: VideoArtifactData }) {
  const [playing, setPlaying] = useState(false)
  const [posterFailed, setPosterFailed] = useState(false)

  // poster_url is stored fully qualified ("/media/..."), so BASE_URL is the only
  // prefix it takes; a second "/media" 404s on every asset that does exist.
  const posterSrc = data.poster_url && !posterFailed ? `${BASE_URL}${data.poster_url}` : null

  return (
    <>
      <div style={SHELL}>
        <div style={STAGE}>
          {posterSrc ? (
            <img
              src={posterSrc}
              alt=""
              loading="lazy"
              onError={() => setPosterFailed(true)}
              style={POSTER}
            />
          ) : null}
          {/* Interactive states live in classes, not in PLAY_BUTTON: inline
              styles cannot express :hover or :focus-visible, and this button
              would otherwise fall back to the UA's 1px grey ring on a dark warm
              surface. Same treatment as CoachPill — amber ring, UA outline off.
              The hover lifts the ring and the fill rather than moving anything;
              nothing here animates a layout property. */}
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${data.title}`}
            // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
            className="flex items-center justify-center border border-[rgba(232,197,160,0.38)] bg-[rgba(28,24,22,0.85)] shadow-[0_12px_32px_-8px_rgba(20,14,12,0.65)] outline-none transition-colors duration-150 hover:border-[rgba(232,197,160,0.62)] hover:bg-[rgba(38,30,26,0.92)] focus-visible:ring-2 focus-visible:ring-(--accent-amber)"
            style={PLAY_BUTTON}
          >
            <Play
              className="h-5 w-5"
              fill={TOKENS.amber}
              stroke={TOKENS.amber}
              strokeWidth={1}
              aria-hidden
            />
          </button>
        </div>
        {/* 12px 16px: the footer's inline padding tracks ARTIFACT_SURFACE's,
            which V3 tightened from 18px to 16px. */}
        {/* The link wraps under the title when the title would be left under 9rem
            (about 19 characters of the eyebrow): at 0.6875rem the link is 137px, and
            beside it a title in a 390px phone's card had 85px and ran to four lines. */}
        <div
          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
          style={{ padding: '12px 16px' }}
        >
          <span style={{ ...ARTIFACT_EYEBROW, ...ARTIFACT_NAME, flex: '1 1 9rem' }}>
            Setup video · {data.title}
          </span>
          {data.href ? (
            <ArtifactAction href={data.href} label="Knowledge Base →" />
          ) : null}
        </div>
      </div>

      {playing ? (
        <MachineVideoOverlay
          src={`${BASE_URL}${data.media_url}`}
          title={data.title}
          onClose={() => setPlaying(false)}
        />
      ) : null}
    </>
  )
}
